// src/pages/BetslipPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useBetSlip } from '../context/BetSlipContext';
import { 
  FiTrash2, 
  FiX, 
  FiPlus, 
  FiMinus, 
  FiShoppingCart,
  FiAlertCircle,
  FiCheck,
  FiDollarSign,
  FiPercent
} from 'react-icons/fi';

const BetslipPage = () => {
  const [bets, setBets] = useState([]);
  const [stake, setStake] = useState('');
  const [potentialWin, setPotentialWin] = useState(0);
  const [totalOdds, setTotalOdds] = useState(1);
  const [quickStakes, setQuickStakes] = useState([5, 10, 25, 50, 100]);
  const [showEmptyState, setShowEmptyState] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [betType, setBetType] = useState('single'); // 'single' or 'multi'
  const navigate = useNavigate();

  const { betslipCount, clearBetSlip, updateBetStake } = useBetSlip();

  useEffect(() => {
    // Load bets from localStorage
    const savedBets = JSON.parse(localStorage.getItem('betslip') || '[]');
    setBets(savedBets);
    setShowEmptyState(savedBets.length === 0);
  }, []);

  useEffect(() => {
    // Calculate total odds and potential win
    if (bets.length > 0) {
      const odds = bets.reduce((acc, bet) => acc * bet.odds, 1);
      setTotalOdds(odds.toFixed(2));
      
      if (stake && parseFloat(stake) > 0) {
        setPotentialWin(parseFloat(stake) * odds);
      } else {
        setPotentialWin(0);
      }
    } else {
      setTotalOdds(1);
      setPotentialWin(0);
    }
  }, [stake, bets]);

  const removeBet = (betId) => {
    const updatedBets = bets.filter(bet => bet.id !== betId);
    setBets(updatedBets);
    localStorage.setItem('betslip', JSON.stringify(updatedBets));
  };

  const handleClearAll = () => {
    setBets([]);
    clearBetSlip();
    setStake('');
    setShowEmptyState(true);
  };

  const handleQuickStake = (amount) => {
    const newStake = parseFloat(stake || 0) + amount;
    setStake(newStake.toString());
  };

  const adjustStake = (amount) => {
    const current = parseFloat(stake || 0);
    const newStake = Math.max(0, current + amount);
    setStake(newStake.toString());
  };

  const handleStakeChange = (value) => {
    // Only allow numbers and one decimal point
    const regex = /^\d*\.?\d*$/;
    if (regex.test(value) || value === '') {
      setStake(value);
    }
  };

  const placeBet = () => {
    if (!stake || parseFloat(stake) <= 0) {
      alert('Please enter a valid stake amount');
      return;
    }

    if (bets.length === 0) {
      alert('Your betslip is empty');
      return;
    }

    if (parseFloat(stake) < 1) {
      alert('Minimum stake is $1');
      return;
    }

    setIsProcessing(true);

    // Simulate API call
    setTimeout(() => {
      const betDetails = {
        id: Date.now(),
        bets: [...bets],
        stake: parseFloat(stake),
        potentialWin,
        totalOdds,
        placedAt: new Date().toISOString(),
        status: 'pending',
        betType: bets.length > 1 ? 'accumulator' : 'single'
      };

      // Save bet history
      const betHistory = JSON.parse(localStorage.getItem('betHistory') || '[]');
      betHistory.unshift(betDetails);
      localStorage.setItem('betHistory', JSON.stringify(betHistory));

      // Clear betslip
      handleClearAll();
      setIsProcessing(false);

      alert(`✅ Bet placed successfully!\nStake: $${parseFloat(stake).toFixed(2)}\nPotential Win: $${potentialWin.toFixed(2)}`);
      
      navigate('/profile?tab=history');
    }, 1500);
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  const getSportIcon = (sport) => {
    switch(sport.toLowerCase()) {
      case 'football': return '⚽';
      case 'basketball': return '🏀';
      case 'tennis': return '🎾';
      case 'cricket': return '🏏';
      case 'esports': return '🎮';
      case 'baseball': return '⚾';
      case 'hockey': return '🏒';
      default: return '🏆';
    }
  };

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-600 rounded-lg">
                <FiShoppingCart className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">My Betslip</h1>
                <p className="text-gray-400 text-sm">
                  {bets.length} selection{bets.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            {/* Mobile Clear All Button */}
            {bets.length > 0 && (
              <button
                onClick={handleClearAll}
                className="sm:hidden p-2 text-red-400 hover:text-red-300"
                aria-label="Clear all bets"
              >
                <FiTrash2 className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Desktop Clear All Button */}
          {bets.length > 0 && (
            <div className="hidden sm:block">
              <button
                onClick={handleClearAll}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
              >
                <FiTrash2 className="w-4 h-4" />
                Clear All
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-col lg:flex-row gap-4">
          {/* Bets List - Left Column */}
          <div className="flex-1">
            {showEmptyState || bets.length === 0 ? (
              <div className="bg-gray-900 rounded-xl p-6 sm:p-8 text-center">
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Your betslip is empty</h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Add selections from sports or live betting to get started
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link 
                    to="/sports" 
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    Browse Sports
                  </Link>
                  <Link 
                    to="/live" 
                    className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    Live Betting
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {bets.map((bet, index) => (
                  <div key={bet.id} className="bg-gray-900 rounded-xl p-4 hover:bg-gray-800 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        {/* Bet Header */}
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{getSportIcon(bet.sport)}</span>
                          <span className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">
                            {bet.sport}
                          </span>
                          {bet.type === 'live' && (
                            <span className="text-xs bg-red-600 text-white px-2 py-1 rounded animate-pulse">
                              LIVE
                            </span>
                          )}
                        </div>
                        
                        {/* Bet Details */}
                        <h4 className="font-bold text-white text-sm sm:text-base mb-1 truncate">
                          {bet.match}
                        </h4>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-green-400 font-semibold text-sm sm:text-base">
                              {bet.selection} @ {bet.odds.toFixed(2)}
                            </p>
                            <p className="text-gray-400 text-xs sm:text-sm">
                              {bet.type === 'home' ? 'Home' : bet.type === 'away' ? 'Away' : bet.type === 'draw' ? 'Draw' : bet.type}
                            </p>
                          </div>
                          
                          {/* Mobile Remove Button */}
                          <button
                            onClick={() => removeBet(bet.id)}
                            className="sm:hidden p-2 text-red-400 hover:text-red-300"
                            aria-label={`Remove ${bet.selection} from betslip`}
                          >
                            <FiX className="w-4 h-4" />
                          </button>
                        </div>
                        
                        {/* Stake Input for Individual Bets (Desktop) */}
                        <div className="hidden sm:block mt-4">
                          <label className="block text-gray-300 text-sm mb-2">
                            Individual Stake (Optional)
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={bet.stake || ''}
                              onChange={(e) => updateBetStake(bet.id, e.target.value)}
                              placeholder="0.00"
                              className="flex-1 bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none"
                            />
                            <span className="text-gray-400 text-sm">$</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Desktop Remove Button */}
                      <button
                        onClick={() => removeBet(bet.id)}
                        className="hidden sm:block p-2 text-red-400 hover:text-red-300"
                        aria-label={`Remove ${bet.selection} from betslip`}
                      >
                        <FiX className="w-5 h-5" />
                      </button>
                    </div>
                    
                    {/* Mobile Stake Input */}
                    <div className="sm:hidden mt-3 pt-3 border-t border-gray-800">
                      <label className="block text-gray-300 text-xs mb-1">
                        Individual Stake
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={bet.stake || ''}
                          onChange={(e) => updateBetStake(bet.id, e.target.value)}
                          placeholder="0.00"
                          className="flex-1 bg-gray-800 text-white px-3 py-1.5 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                        />
                        <span className="text-gray-400 text-sm">$</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Betting Tips */}
            {bets.length > 0 && (
              <div className="mt-6 sm:mt-8 bg-gray-900 rounded-xl p-4 sm:p-6">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <FiAlertCircle className="w-5 h-5 text-yellow-400" />
                  Betting Tips & Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="p-3 bg-gradient-to-br from-blue-900/30 to-transparent border border-blue-800/30 rounded-lg">
                    <h4 className="font-bold text-white mb-2 text-sm">📊 Bet Types</h4>
                    <ul className="text-gray-300 text-xs space-y-1">
                      <li>• Single: Bet on one selection</li>
                      <li>• Accumulator: Multiple selections combined</li>
                      <li>• System: Multiple bets in one</li>
                    </ul>
                  </div>
                  <div className="p-3 bg-gradient-to-br from-green-900/30 to-transparent border border-green-800/30 rounded-lg">
                    <h4 className="font-bold text-white mb-2 text-sm">💰 Responsible Betting</h4>
                    <ul className="text-gray-300 text-xs space-y-1">
                      <li>• Set a budget and stick to it</li>
                      <li>• Never chase losses</li>
                      <li>• Take regular breaks</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bet Calculator - Right Column */}
          {bets.length > 0 && (
            <div className="lg:w-96 w-full">
              <div className="bg-gray-900 rounded-xl p-4 sm:p-6 sticky top-24">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <FiDollarSign className="w-5 h-5 text-green-400" />
                  Bet Calculator
                </h3>
                
                {/* Quick Stakes */}
                <div className="mb-6">
                  <label className="block text-gray-300 mb-3 text-sm sm:text-base">Quick Stakes</label>
                  <div className="grid grid-cols-5 gap-2">
                    {quickStakes.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => handleQuickStake(amount)}
                        className="py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
                      >
                        +${amount}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Stake Input */}
                <div className="mb-6">
                  <label className="block text-gray-300 mb-2 text-sm sm:text-base">Stake Amount ($)</label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                      <FiDollarSign className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={stake}
                      onChange={(e) => handleStakeChange(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-10 pr-20 py-3 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-green-500 focus:outline-none text-lg"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                      <button
                        onClick={() => adjustStake(-1)}
                        className="p-1 bg-gray-700 hover:bg-gray-600 rounded"
                        aria-label="Decrease stake by $1"
                      >
                        <FiMinus className="w-4 h-4 text-white" />
                      </button>
                      <button
                        onClick={() => adjustStake(1)}
                        className="p-1 bg-gray-700 hover:bg-gray-600 rounded"
                        aria-label="Increase stake by $1"
                      >
                        <FiPlus className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                  <div className="text-gray-400 text-xs mt-2">
                    Min: $1 • Max: $10,000
                  </div>
                </div>

                {/* Bet Summary */}
                <div className="space-y-3 sm:space-y-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm sm:text-base">Number of Bets:</span>
                    <span className="text-white font-medium">{bets.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm sm:text-base">Bet Type:</span>
                    <span className="text-white font-medium">
                      {bets.length === 1 ? 'Single' : `${bets.length}-Fold Accumulator`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm sm:text-base">Total Odds:</span>
                    <span className="text-white font-medium flex items-center gap-1">
                      <FiPercent className="w-4 h-4" />
                      {totalOdds}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm sm:text-base">Stake:</span>
                    <span className="text-white font-medium">${stake || '0.00'}</span>
                  </div>
                  <div className="border-t border-gray-700 pt-3 sm:pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-300">Potential Win:</span>
                      <span className="text-2xl font-bold text-green-400">
                        ${formatCurrency(potentialWin)}
                      </span>
                    </div>
                    <div className="text-gray-400 text-xs mt-1 text-right">
                      Includes stake
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <button
                  onClick={placeBet}
                  disabled={!stake || parseFloat(stake) < 1 || isProcessing}
                  className={`w-full py-4 rounded-lg font-bold text-lg mb-4 transition-all flex items-center justify-center gap-2 ${
                    !stake || parseFloat(stake) < 1 || isProcessing
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white shadow-lg hover:shadow-green-500/30'
                  }`}
                >
                  {isProcessing ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FiCheck className="w-5 h-5" />
                      Place Bet
                    </>
                  )}
                </button>

                <Link 
                  to="/wallet" 
                  className="block w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold text-center transition-all"
                >
                  Deposit Funds
                </Link>

                {/* Quick Info */}
                <div className="mt-6 pt-6 border-t border-gray-800">
                  <div className="text-xs text-gray-400 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span>All bets are placed in real-time</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>24/7 customer support available</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                      <span>Winnings paid within 24 hours</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Bottom Action Bar */}
        {bets.length > 0 && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 p-3 z-30 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <div className="text-sm text-gray-400">Potential Win</div>
                <div className="text-lg font-bold text-green-400">
                  ${formatCurrency(potentialWin)}
                </div>
              </div>
              <button
                onClick={placeBet}
                disabled={!stake || parseFloat(stake) < 1 || isProcessing}
                className={`px-6 py-3 rounded-lg font-bold transition-all ${
                  !stake || parseFloat(stake) < 1 || isProcessing
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white'
                }`}
              >
                {isProcessing ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm">Processing...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FiCheck className="w-4 h-4" />
                    <span className="text-sm">Place Bet</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Empty State Suggestions */}
        {showEmptyState && (
          <div className="mt-8">
            <h3 className="text-lg font-bold text-white mb-4">Popular Markets</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { sport: 'football', match: 'Premier League', odds: '2.50' },
                { sport: 'basketball', match: 'NBA Playoffs', odds: '1.85' },
                { sport: 'tennis', match: 'Grand Slam', odds: '3.20' },
                { sport: 'esports', match: 'CS:GO Major', odds: '2.10' }
              ].map((market, index) => (
                <div key={index} className="bg-gray-900 rounded-xl p-4 hover:bg-gray-800 transition-colors cursor-pointer">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">{getSportIcon(market.sport)}</span>
                    <span className="text-sm text-gray-400">{market.sport}</span>
                  </div>
                  <h4 className="font-bold text-white text-sm mb-2">{market.match}</h4>
                  <div className="text-green-400 font-semibold">Avg odds: {market.odds}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add padding bottom for mobile bottom bar */}
      {bets.length > 0 && (
        <div className="pb-20 lg:pb-0"></div>
      )}

      <Footer />
    </div>
  );
};

export default BetslipPage;